import React, { useMemo } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { FiltersTypes } from "@common/types/filters/general";
import { useAllInvoiceTemplatesForEntities } from "@api/invoiceTemplate.api";
import { useEntities } from "@hooks/useEntities";
import {
    resolveTemplateFilterEntityIds,
    UNKNOWN_INVOICE_SOURCE_TEMPLATE_LABEL,
} from "../Tabs/InvoiceSourceTemplateFilter/invoiceSourceTemplateFilter.lib";
import { formatFilterLabel } from "./formatFilterLabel";

interface Props {
    filters: Pick<FiltersTypes, "sourceTemplateIds" | "entityIds">;
}

export const InvoiceSourceTemplateFilterLabel: React.FC<Props> = ({
    filters,
}) => {
    const { sourceTemplateIds, entityIds: selectedEntityIds } = filters;
    const businessEntities = useEntities({ onlyBusiness: true });
    const entityIds = useMemo(
        () =>
            resolveTemplateFilterEntityIds({
                selectedEntityIds,
                businessEntities,
            }),
        [selectedEntityIds, businessEntities],
    );
    const { templates, isLoading, isError } = useAllInvoiceTemplatesForEntities(
        {
            entityIds,
            enabled: !!sourceTemplateIds?.length,
        },
    );

    const labels = useMemo(() => {
        const nameById = new Map(
            templates.map((template) => [template.id, template.name]),
        );
        return (sourceTemplateIds ?? []).map(
            (id) => nameById.get(id) ?? UNKNOWN_INVOICE_SOURCE_TEMPLATE_LABEL,
        );
    }, [templates, sourceTemplateIds]);

    if (!sourceTemplateIds?.length) {
        return null;
    }

    if (isLoading) {
        return null;
    }

    if (isError) {
        return pluralizeWithCount("template", sourceTemplateIds.length);
    }

    return formatFilterLabel(labels);
};
